import '../../styles/_time-card.scss';

import { skipToken } from '@reduxjs/toolkit/dist/query';
import React, { useEffect, useMemo } from 'react';
import { useErrorBoundary } from 'react-error-boundary';

import { CleTimeRecord } from '../../datasource/generated';
import { useGetCleTimeRecordsQuery } from '../../datasource/queries/cle';
import { useAppSelector } from '../../hooks';
import Loader from '../Loader/Loader';
import { ErrorContainer } from '../MainError/MainError';
import withPLIErrorBoundary from '../PliErrorBoundary/PliErrorBoundary';
import { TimeCard } from '../Timecard/TimeCard';

export type ITimeCardsProps = {
    onClose: (() => void) | null;
    onTimeCardsLoaded: (newValue: boolean) => void;
};

export const CreditProgressTimeCardsBase = ({ onClose, onTimeCardsLoaded: onHasTimeCardsLoadedChange }: ITimeCardsProps) => {
    const encryptedRegistrationId= useAppSelector((state) => state.requestCredit.encryptedRegistrationId);
    const { showBoundary } = useErrorBoundary();
    const {
        data: timeRecords,
        isLoading: isLoadingTimeRecords,
        isSuccess: isGetTimeCardsSuccess,
        isError: isGetTimeCardsError,
    } = useGetCleTimeRecordsQuery(encryptedRegistrationId ?? skipToken);

    useEffect(() => {
        onHasTimeCardsLoadedChange(isGetTimeCardsSuccess);
    }, [isGetTimeCardsSuccess, onHasTimeCardsLoadedChange]);

    const launchDetails = useAppSelector((state) => state.player.launchDetails);

    const records = useMemo(() => {
        if (timeRecords === undefined)
            return {
                liveCleRecords: undefined,
                onDemandCleRecords: undefined,
            };

        const liveCleRecords = timeRecords.filter(timeRecord => timeRecord.isLive);
        const onDemandCleRecords = timeRecords.filter(timeRecord => !timeRecord.isLive);
        
        if (!launchDetails.isWebcast && liveCleRecords.length) {
            const recordTemplates : CleTimeRecord[] = timeRecords
                .filter((value, index, self) => 
                    self.findIndex(v => v.prgSegment_SK === value.prgSegment_SK) === index)
                .map(value => {
                    return {
                        ...value,
                        percentComplete: 0,
                        isLive: false,
                    };
                });

            const onDemandRecords = recordTemplates.map(recordTemplate => {
                const onDemandRecord = onDemandCleRecords.find(
                    record => record.prgSegment_SK === recordTemplate.prgSegment_SK
                );

                return {
                    ...recordTemplate,
                    trackCle: onDemandRecord?.trackCle ?? recordTemplate.trackCle,
                    percentComplete: onDemandRecord?.percentComplete ?? 0,
                    isLive: false,
                };
            });

            const liveRecords = recordTemplates.map(recordTemplate => {
                return {
                    ...recordTemplate,
                    percentComplete: liveCleRecords.find(x => x.prgSegment_SK === recordTemplate.prgSegment_SK)?.percentComplete ?? 0,
                    isLive: true,
                };
            });

            return {
                liveCleRecords: liveRecords,
                onDemandCleRecords: onDemandRecords,
            };
        }
        else {
            return {
                liveCleRecords: liveCleRecords,
                onDemandCleRecords: onDemandCleRecords,
            };
        }
    }, [launchDetails.isWebcast, timeRecords]);

    const hasLiveCleRecords: boolean = (records?.liveCleRecords && records?.liveCleRecords.length > 0) ?? false;
    const hasOnDemandCleRecords: boolean = (records?.onDemandCleRecords && records?.onDemandCleRecords.length > 0) ?? false;
    const hasBothLiveAndODRecords: boolean = hasLiveCleRecords && hasOnDemandCleRecords;
    const hasEitherLiveOrODRecords: boolean = (hasLiveCleRecords || hasOnDemandCleRecords) && !(hasLiveCleRecords && hasOnDemandCleRecords);

    const isSingleSegment = (launchDetails.segments?.length ?? 0) <= 1;

    if (isGetTimeCardsError) {
        showBoundary(new Error('Error fetching Time Cards'));
    }

    const handleFocus = (e: React.FocusEvent<HTMLDivElement>) => {
        e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    return (
        <>
            {isLoadingTimeRecords && (
                <div className='h-75'>
                    <Loader />
                </div>
            )}

            {isGetTimeCardsSuccess ? (
                hasBothLiveAndODRecords ? (
                    <div>
                        <div className='time-card__type-heading paragraph-2'>On-demand</div>

                        <ul className='time-cards'>
                            {records?.onDemandCleRecords?.map((record) => (
                                <TimeCard timeRecord={record} key={record.prgSegment_SK ?? ''} onClose={onClose} isSingleSegment={isSingleSegment} />
                            ))}

                        </ul>

                        <div className='time-card__type-heading paragraph-2 mt-3'>Watched live</div>

                        <ul className='time-cards'>
                            {records?.liveCleRecords?.map((record) => (
                                <TimeCard timeRecord={record} key={record.prgSegment_SK ?? ''} onClose={onClose} isSingleSegment={isSingleSegment} />
                            ))}
                        </ul>
                    </div>
                ) : (hasEitherLiveOrODRecords ? (
                    <div className='time-cards' onFocus={handleFocus}>
                        <ul className='time-cards'>
                            {hasLiveCleRecords && records.liveCleRecords?.map((record) => (
                                <TimeCard timeRecord={record} key={record.prgSegment_SK ?? ''} onClose={onClose} isSingleSegment={isSingleSegment} />
                            ))}
                            {hasOnDemandCleRecords && records.onDemandCleRecords?.map((record) => (
                                <TimeCard timeRecord={record} key={record.prgSegment_SK ?? ''} onClose={onClose} isSingleSegment={isSingleSegment} />
                            ))}
                        </ul>
                    </div>
                ) : null)
            ) : null}
        </>
    );
};

export const CreditProgressTimeCards = withPLIErrorBoundary(CreditProgressTimeCardsBase, null, {
    errorContainer: ErrorContainer.LargePanel,
});
